import React, {
  useState,
  useMemo,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { Dropdown, Menu, Radio } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import UtcDatePicker from "components/Common/UtcDatePicker";
// Context
import { PricerContext } from "contexts/PricerContext";
import { GlobalContext } from "contexts/GlobalContext";
// Components
import AddOptionModal from "../AddOptionModal";
// Helpers
import { FIELD_NAMES, DEFAULT_SORT } from "constants/pricerTable";
import { storeInLocalStorage } from "helpers/Pricer/Table";
import {
  PRICER_DATE_SELECTED,
  PRICER_DATAFunc,
  PRICER_FILTER_BUTTON_SELECTED,
} from "helpers/Pricer/utils";
// Style
import "styles/PricerAuditAgGridLayout.scss";
import "./FilterButtons.scss";
import classnames from "classnames";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  DESCRIPTIONS,
  ELEMENTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import {
  AddButton,
  SecondaryButton,
} from "components/Common/CustomButtons/CustomButtons";
import CSVImportModal from "components/Pricer/CSVImportModal";
import QuickAdd from "components/QuickAdd/QuickAdd";
import { prefetchShorthandGrammar } from "components/QuickAdd/useShorthandGrammar";
import TradeCaptureModal from "components/TradeCapture/TradeCaptureModal";
import VolSurfaceComparison from "components/Common/FilterButtons/VolSurfaceComparison";
import { useQueryClient } from "@tanstack/react-query";
import {
  calendarDate,
  isUtcToday,
  utcCalendarMoment,
  utcToday,
} from "helpers/utcCalendarDate";

export const FILTER_BUTTONS_NAMES = {
  RESET_SORT: "Reset Sort",
  REFRESH: "Refresh",
  ALL: "All",
  ADD_OPTION: "Add Option",
  VOL_SURFACE_COMPARISON: "Vol Surface Comparison",
};

export const SUMMARY_IDS = "summaryIds";

// Toolbar item keys in left-to-right order. Items overflow into the "Pricer
// Tools" menu from the right (lowest priority first) as space runs out.
const ITEM_KEYS = {
  COMMODITIES: "commodities",
  ADD_OPTION: "add-option",
  RESET_SORT: "reset-sort",
  DATE: "date",
  COLLAPSE_ALL: "collapse-all",
  CSV_IMPORT: "csv-import",
  QUICK_ADD: "quick-add",
  IMPORT_TRADES: "import-trades",
  VOL_SURFACE_COMPARISON: "vol-surface-comparison",
};

// Visual left-to-right render order on the toolbar.
const TOOLBAR_ORDER = [
  ITEM_KEYS.COMMODITIES,
  ITEM_KEYS.ADD_OPTION,
  ITEM_KEYS.RESET_SORT,
  ITEM_KEYS.DATE,
  ITEM_KEYS.COLLAPSE_ALL,
  ITEM_KEYS.CSV_IMPORT,
  ITEM_KEYS.QUICK_ADD,
  ITEM_KEYS.IMPORT_TRADES,
  ITEM_KEYS.VOL_SURFACE_COMPARISON,
];

// Overflow priority: items earlier in this list are the LAST to move into the
// menu as space shrinks. DATE is first so it always stays on the bar the longest.
const TOOLBAR_OVERFLOW_PRIORITY = [
  ITEM_KEYS.DATE,
  ITEM_KEYS.COMMODITIES,
  ITEM_KEYS.ADD_OPTION,
  ITEM_KEYS.RESET_SORT,
  ITEM_KEYS.COLLAPSE_ALL,
  ITEM_KEYS.CSV_IMPORT,
  ITEM_KEYS.QUICK_ADD,
  ITEM_KEYS.IMPORT_TRADES,
  ITEM_KEYS.VOL_SURFACE_COMPARISON,
];

// Grouping for the overflow menu, matching the requested layout.
const MENU_GROUPS = [
  { title: "Filter", keys: [ITEM_KEYS.COMMODITIES] },
  {
    title: "CSV Imports and Option Capture",
    keys: [
      ITEM_KEYS.CSV_IMPORT,
      ITEM_KEYS.QUICK_ADD,
      ITEM_KEYS.IMPORT_TRADES,
      ITEM_KEYS.VOL_SURFACE_COMPARISON,
    ],
  },
  { title: "Collapse All", keys: [ITEM_KEYS.COLLAPSE_ALL, ITEM_KEYS.RESET_SORT] },
  { title: "Date", keys: [ITEM_KEYS.DATE] }, // Date is last to overflow, shown at bottom of menu
];

const ITEM_GAP = 8;

const getNavTabsLeft = () => {
  const tabsEl = document.getElementById("header-tabs");
  if (tabsEl) return tabsEl.getBoundingClientRect().left;
  // Fallback: reserve the right ~38% of the screen for navbar tabs/logo.
  return window.innerWidth * 0.62;
};

const FilterButtons = () => {
  //Context
  const {
    state: { pricerTablesData, pricerOptInfo, pricerRefAgGrid, dateSelected },
    isOptionRowLoading,
    isOptionRowRefetching,
    isOptionRowFetching,
    setDateSelected,
    setPricerOperation,
    setIsCalculationDateChanged,
    refetchOptionRow,
  } = useContext(PricerContext);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const queryClient = useQueryClient();

  // Warm the shorthand grammar cache so Quick Add opens ready to parse.
  useEffect(() => {
    prefetchShorthandGrammar(queryClient);
  }, [queryClient]);

  //States
  const [productsData, setProductsData] = useState([]);
  const [isAddOptionModalVisible, setIsAddOptionModalVisible] = useState(false);
  const [buttonSelected, setButtonSelected] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [quickAddFocusKey, setQuickAddFocusKey] = useState(0);
  const [isTradeCaptureOpen, setIsTradeCaptureOpen] = useState(false);
  const [isVolComparisonOpen, setIsVolComparisonOpen] = useState(false);
  const [isCompactMenuOpen, setIsCompactMenuOpen] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(() => [ITEM_KEYS.ADD_OPTION]);
  const [isToolbarReady, setIsToolbarReady] = useState(false);
  const [reserveRight, setReserveRight] = useState(() => {
    if (typeof window === "undefined") return 0;
    const navTabsLeft = getNavTabsLeft();
    return Math.max(window.innerWidth - navTabsLeft + 12, 0);
  });

  const measureRefs = useRef({});
  const menuMeasureRef = useRef(null);
  const containerRef = useRef(null);
  const menuOpenRef = useRef(false);
  const datePickerRef = useRef();
  const resizeRafRef = useRef(0);
  const addOptionOpenTimerRef = useRef(null);
  const isQuickAddOpenRef = useRef(false);
  isQuickAddOpenRef.current = isQuickAddOpen;

  const filterInstance =
    pricerRefAgGrid?.api &&
    pricerRefAgGrid?.api.getFilterInstance(FIELD_NAMES.PRODUCT);

  const applySelectedFilter = (buttonClicked) => {
    if (buttonClicked !== FILTER_BUTTONS_NAMES.ALL) {
      filterInstance?.setModel({ values: [buttonClicked] });
    }
    if (buttonClicked === FILTER_BUTTONS_NAMES.ALL) {
      filterInstance?.setModel({ values: null });
    }
    pricerRefAgGrid?.api && pricerRefAgGrid.api.onFilterChanged();
  };

  const storeValuesSelectedIntoLocalStorage = (key, value) => {
    const data = {
      ...pricerOptInfo,
      [key]: value,
    };
    setPricerOperation(data);
  };
  const onFilterButtonClick = (buttonClicked) => {
    setButtonSelected(buttonClicked);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_COMMODITY_FILTER_BUTTON,
      extraParams: { filter: buttonClicked },
    });
    if (!filterInstance) return;
    storeValuesSelectedIntoLocalStorage(
      PRICER_FILTER_BUTTON_SELECTED,
      buttonClicked
    );
    applySelectedFilter(buttonClicked);
  };

  const showProductsButtons = () => {
    if (productsData.length === 0) return <></>;
    return productsData.map((item, index) => {
      return (
        <Radio.Button key={`${item}-${index}`} value={item}>
          {item}
        </Radio.Button>
      );
    });
  };

  // Derived from the already-fetched pricer rows instead of firing a second
  // request against /option-rows just to list distinct products.
  useEffect(() => {
    if (!pricerTablesData || pricerTablesData.length === 0) return;
    const filterButtons = [
      ...new Set(pricerTablesData.map((item) => item[FIELD_NAMES.PRODUCT])),
    ];
    setProductsData([
      FILTER_BUTTONS_NAMES.ALL,
      ...filterButtons.filter((product) => product),
    ]);
  }, [pricerTablesData]);

  useEffect(
    () => () => {
      if (addOptionOpenTimerRef.current) {
        clearTimeout(addOptionOpenTimerRef.current);
      }
    },
    []
  );

  const onResetButtonClick = () => {
    pricerRefAgGrid.columnApi.applyColumnState({
      state: DEFAULT_SORT,
      defaultState: { sort: null },
    });
  };

  const storeSummaryIdsInLocalStorage = (pricerTablesData) => {
    const summaryIds =
      pricerTablesData && pricerTablesData.map((item) => item.summary_id);
    storeInLocalStorage(SUMMARY_IDS, JSON.stringify(summaryIds));
  };

  const previousSelectedFilter =
    PRICER_DATAFunc()?.[PRICER_FILTER_BUTTON_SELECTED];

  //2 Effect To Store Selected Button And Get Value From Local Storage
  useEffect(() => {
    if (
      !previousSelectedFilter ||
      pricerTablesData.length === 0 ||
      !pricerTablesData
    )
      return;
    setButtonSelected(previousSelectedFilter);
  }, [previousSelectedFilter, pricerTablesData.length]);

  useEffect(() => {
    if (!buttonSelected || pricerTablesData.length === 0 || !pricerTablesData)
      return;
    applySelectedFilter(buttonSelected);
  }, [buttonSelected, pricerTablesData.length]);

  const onDatePickerChange = (date, dateString) => {
    // Clear (x) returns to the UTC calendar day. A picked day stays the
    // YYYY-MM-DD from the picker; do not format the moment, which can move it.
    const finalDate =
      date == null || !dateString
        ? utcToday()
        : calendarDate(dateString) ?? utcToday();

    storeValuesSelectedIntoLocalStorage(PRICER_DATE_SELECTED, finalDate);
    setDateSelected(finalDate);
    setIsDatePickerOpen(false);
    datePickerRef.current && datePickerRef.current.blur();
    setIsCalculationDateChanged(1);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_CALCULATION_DATE_CHANGE,
      extraParams: {
        change_type: date === null || !dateString ? "clear" : "set",
      },
    });
  };
  const isDisabled =
    isOptionRowFetching || isOptionRowLoading || isOptionRowRefetching;

  const closeCompactMenu = useCallback(() => {
    menuOpenRef.current = false;
    setIsCompactMenuOpen(false);
  }, []);

  const handleResetSortClick = useCallback(() => {
    onResetButtonClick();
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_RESET_SORT_BUTTON,
    });
    closeCompactMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricerRefAgGrid, closeCompactMenu]);

  const handleCollapseAllClick = useCallback(() => {
    pricerRefAgGrid?.api && pricerRefAgGrid.api.collapseAll();
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_COLLAPSE_ALL_BUTTON,
    });
    closeCompactMenu();
  }, [pricerRefAgGrid, closeCompactMenu]);

  const handleCSVImportClick = useCallback(() => {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_CSV_IMPORT_OPEN,
    });
    setIsCSVImportModalOpen(true);
    closeCompactMenu();
  }, [closeCompactMenu]);

  const openQuickAdd = useCallback(
    (openSource = "button") => {
      // Only count a real open (closed → open). Re-pressing / or the button
      // while open just refocuses the bar.
      if (!isQuickAddOpenRef.current) {
        sendEvent({
          category: CATEGORIES.PRICER,
          context: CONTEXTS.PRICER,
          action: ACTIONS.CLICK,
          component: COMPONENT_NAMES.BUTTON,
          eventName: EVENT_NAMES.PRICER_QUICK_ADD_OPEN,
          extraParams: {
            open_source: openSource,
          },
        });
      }
      setIsQuickAddOpen(true);
      setQuickAddFocusKey((k) => k + 1);
      closeCompactMenu();
    },
    [closeCompactMenu]
  );

  const handleQuickAddClick = useCallback(() => {
    openQuickAdd("button");
  }, [openQuickAdd]);

  // Global "/" opens Quick Add, or focuses the bar if already open.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      openQuickAdd("hotkey");
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openQuickAdd]);

  const handleImportTradesClick = useCallback(() => {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_IMPORT_TRADES_OPEN,
    });
    setIsTradeCaptureOpen(true);
    closeCompactMenu();
  }, [closeCompactMenu]);

  const handleVolSurfaceComparisonClick = useCallback(() => {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_VOL_SURFACE_COMPARISON_OPEN,
    });
    setIsVolComparisonOpen(true);
    closeCompactMenu();
  }, [closeCompactMenu]);

  const handleAddOptionClick = useCallback(
    (event) => {
      const native = event?.domEvent ?? event;
      native?.stopPropagation?.();
      closeCompactMenu();
      storeSummaryIdsInLocalStorage(pricerTablesData);
      sendEvent({
        category: CATEGORIES.PRICER,
        description: DESCRIPTIONS.ADD_OPTION,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        element: ELEMENTS.ADD_OPTION,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_ADD_OPTION_OPEN,
      });
      // Next tick: this click must not land on the modal mask (two-click open).
      if (addOptionOpenTimerRef.current) {
        clearTimeout(addOptionOpenTimerRef.current);
      }
      addOptionOpenTimerRef.current = window.setTimeout(() => {
        addOptionOpenTimerRef.current = null;
        setIsAddOptionModalVisible(true);
      }, 0);
    },
    [pricerTablesData, closeCompactMenu]
  );

  const handleCompactCommodityClick = useCallback(
    (product) => {
      onFilterButtonClick(product);
      closeCompactMenu();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeCompactMenu]
  );

  const datePickerClassName = classnames(
    "calc-data",
    dateSelected && !isUtcToday(dateSelected) && "filled--data__background"
  );

  const renderDatePicker = (className = datePickerClassName, forMeasure = false) => (
    <UtcDatePicker
      disabled={isDisabled}
      ref={forMeasure ? undefined : datePickerRef}
      onOpenChange={setIsDatePickerOpen}
      value={dateSelected ? utcCalendarMoment(dateSelected) : ""}
      onChange={onDatePickerChange}
      format="YYYY-MM-DD"
      placeholder={"Calculation Date"}
      popupStyle={{ background: "black" }}
      popupClassName={dark_Theme || false ? "darkCalender" : ""}
      style={{ color: "white !important" }}
      className={className}
      onClick={(event) => event.stopPropagation()}
    />
  );

  // Inline (on-bar) renderers, keyed by item.
  const inlineRenderers = {
    [ITEM_KEYS.COMMODITIES]: () =>
      productsData.length > 0 ? (
        <Radio.Group
          key="commodities"
          className="buttons-container__small"
          value={buttonSelected}
          onChange={(e) => onFilterButtonClick(e.target.value)}
        >
          {showProductsButtons()}
        </Radio.Group>
      ) : null,
    [ITEM_KEYS.ADD_OPTION]: () => (
      <AddButton key="add-option" onClick={handleAddOptionClick}>
        {FILTER_BUTTONS_NAMES.ADD_OPTION}
      </AddButton>
    ),
    [ITEM_KEYS.RESET_SORT]: () => (
      <SecondaryButton
        key="reset-sort"
        className="secondary-button--white"
        onClick={handleResetSortClick}
      >
        {FILTER_BUTTONS_NAMES.RESET_SORT}
      </SecondaryButton>
    ),
    [ITEM_KEYS.DATE]: (forMeasure = false) => (
      <span key="date">{renderDatePicker(datePickerClassName, forMeasure)}</span>
    ),
    [ITEM_KEYS.COLLAPSE_ALL]: () =>
      productsData.length > 0 ? (
        <SecondaryButton
          key="collapse-all"
          className="secondary-button--white"
          onClick={handleCollapseAllClick}
        >
          Collapse All
        </SecondaryButton>
      ) : null,
    [ITEM_KEYS.CSV_IMPORT]: () => (
      <SecondaryButton
        key="csv-import"
        className="secondary-button--white"
        onClick={handleCSVImportClick}
      >
        CSV Import
      </SecondaryButton>
    ),
    [ITEM_KEYS.QUICK_ADD]: () => (
      <SecondaryButton
        key="quick-add"
        className="secondary-button--white"
        onClick={handleQuickAddClick}
      >
        Quick Add
      </SecondaryButton>
    ),
    [ITEM_KEYS.IMPORT_TRADES]: () => (
      <SecondaryButton
        key="import-trades"
        className="secondary-button--white"
        onClick={handleImportTradesClick}
      >
        Option Capture
      </SecondaryButton>
    ),
    [ITEM_KEYS.VOL_SURFACE_COMPARISON]: () => (
      <SecondaryButton
        key="vol-surface-comparison"
        className="secondary-button--white"
        onClick={handleVolSurfaceComparisonClick}
      >
        {FILTER_BUTTONS_NAMES.VOL_SURFACE_COMPARISON}
      </SecondaryButton>
    ),
  };

  // Menu (overflow) renderers, keyed by item.
  const menuRenderers = {
    [ITEM_KEYS.CSV_IMPORT]: () => (
      <Menu.Item key="csv-import" onClick={handleCSVImportClick}>
        CSV Import
      </Menu.Item>
    ),
    [ITEM_KEYS.QUICK_ADD]: () => (
      <Menu.Item key="quick-add" onClick={handleQuickAddClick}>
        Quick Add
      </Menu.Item>
    ),
    [ITEM_KEYS.IMPORT_TRADES]: () => (
      <Menu.Item key="import-trades" onClick={handleImportTradesClick}>
        Option Capture
      </Menu.Item>
    ),
    [ITEM_KEYS.VOL_SURFACE_COMPARISON]: () => (
      <Menu.Item
        key="vol-surface-comparison"
        onClick={handleVolSurfaceComparisonClick}
      >
        {FILTER_BUTTONS_NAMES.VOL_SURFACE_COMPARISON}
      </Menu.Item>
    ),
    [ITEM_KEYS.COLLAPSE_ALL]: () => (
      <Menu.Item key="collapse-all" onClick={handleCollapseAllClick}>
        Collapse All
      </Menu.Item>
    ),
    [ITEM_KEYS.RESET_SORT]: () => (
      <Menu.Item key="reset-sort" onClick={handleResetSortClick}>
        {FILTER_BUTTONS_NAMES.RESET_SORT}
      </Menu.Item>
    ),
    [ITEM_KEYS.DATE]: () => (
      <Menu.Item
        key="calculation-date"
        className="pricer-filter-compact-menu__date-item"
      >
        <div
          className="pricer-filter-compact-menu__date-picker"
          onClick={(event) => event.stopPropagation()}
        >
          {renderDatePicker(
            classnames(
              datePickerClassName,
              "pricer-filter-compact-menu__date-input"
            )
          )}
        </div>
      </Menu.Item>
    ),
    [ITEM_KEYS.ADD_OPTION]: () => (
      <Menu.Item key="add-option" onClick={handleAddOptionClick}>
        {FILTER_BUTTONS_NAMES.ADD_OPTION}
      </Menu.Item>
    ),
    [ITEM_KEYS.COMMODITIES]: () => (
      <Menu.Item
        key="commodities-filter"
        className="pricer-filter-compact-menu__commodity-row"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="pricer-filter-compact-menu__commodity-pills"
        >
          <Radio.Group
            value={buttonSelected}
            onChange={(e) => handleCompactCommodityClick(e.target.value)}
          >
            {productsData.map((product, index) => (
              <Radio.Button key={`${product}-${index}`} value={product}>
                {product}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      </Menu.Item>
    ),
  };

  // Keys that are actually available (commodities/collapse-all need products).
  const availableKeys = useMemo(
    () =>
      TOOLBAR_ORDER.filter((key) => {
        if (
          (key === ITEM_KEYS.COMMODITIES || key === ITEM_KEYS.COLLAPSE_ALL) &&
          productsData.length === 0
        ) {
          return false;
        }
        return true;
      }),
    [productsData.length]
  );

  // Keys that are always pinned to the inline toolbar and never overflow.
  const PINNED_KEYS = [ITEM_KEYS.ADD_OPTION];

  // Measure available width up to the navbar tabs and decide how many items fit.
  const recomputeOverflow = useCallback(
    ({ requireMeasures = false } = {}) => {
      const navTabsLeft = getNavTabsLeft();
      setReserveRight(Math.max(window.innerWidth - navTabsLeft + 12, 0));

      const available = navTabsLeft - 15 - 12; // left inset + gap before tabs

      if (requireMeasures && availableKeys.length > 0) {
        const hasValidMeasures = availableKeys.every(
          (key) => (measureRefs.current[key]?.offsetWidth ?? 0) > 0
        );
        if (!hasValidMeasures) return false;
      }

      // Pinned items always consume space; the rest compete for what remains.
      const pinnedWidths = PINNED_KEYS.filter((k) =>
        availableKeys.includes(k)
      ).reduce(
        (sum, k) =>
          sum + (measureRefs.current[k]?.offsetWidth || 0) + ITEM_GAP,
        0
      );

      // Flex keys sorted by overflow priority (highest priority = last to leave).
      const flexKeys = TOOLBAR_OVERFLOW_PRIORITY.filter(
        (k) => availableKeys.includes(k) && !PINNED_KEYS.includes(k)
      );
      const widths = flexKeys.map((key) => ({
        key,
        width: measureRefs.current[key]?.offsetWidth || 0,
      }));

      const totalAll =
        pinnedWidths +
        widths.reduce((sum, item) => sum + item.width, 0) +
        ITEM_GAP * Math.max(widths.length - 1, 0);

      let fittedKeys;
      if (totalAll <= available) {
        fittedKeys = availableKeys;
      } else {
        const menuWidth = menuMeasureRef.current?.offsetWidth || 120;
        let used = menuWidth + ITEM_GAP + pinnedWidths;
        fittedKeys = [];
        for (let i = 0; i < widths.length; i += 1) {
          const next = widths[i].width + ITEM_GAP;
          if (used + next <= available) {
            used += next;
            fittedKeys.push(widths[i].key);
          }
          // Don't break — lower-priority items may still fit if a wide one didn't.
        }
        PINNED_KEYS.forEach((k) => {
          if (availableKeys.includes(k)) fittedKeys.push(k);
        });
      }

      const nextVisible = TOOLBAR_ORDER.filter((k) => fittedKeys.includes(k));

      setVisibleKeys((prev) => {
        if (
          prev.length === nextVisible.length &&
          prev.every((key, idx) => key === nextVisible[idx])
        ) {
          return prev;
        }
        return nextVisible;
      });

      setIsToolbarReady(true);
      return true;
    },
    [availableKeys]
  );

  const scheduleRecomputeOverflow = useCallback(
    (options) => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = 0;
        recomputeOverflow(options);
      });
    },
    [recomputeOverflow]
  );

  useLayoutEffect(() => {
    let initialRafId = 0;
    const runInitial = () => {
      if (recomputeOverflow({ requireMeasures: true })) return;
      initialRafId = requestAnimationFrame(runInitial);
    };
    runInitial();

    const onResize = () => scheduleRecomputeOverflow();

    window.addEventListener("resize", onResize);

    const observedElements = [];
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(onResize);
      [containerRef.current, document.getElementById("header-tabs")].forEach(
        (el) => {
          if (el) {
            resizeObserver.observe(el);
            observedElements.push(el);
          }
        }
      );
    }

    return () => {
      if (initialRafId) cancelAnimationFrame(initialRafId);
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      window.removeEventListener("resize", onResize);
      if (resizeObserver) {
        observedElements.forEach((el) => resizeObserver.unobserve(el));
        resizeObserver.disconnect();
      }
    };
  }, [recomputeOverflow, scheduleRecomputeOverflow, availableKeys]);

  useEffect(() => {
    scheduleRecomputeOverflow();
  }, [dateSelected, dark_Theme, buttonSelected, scheduleRecomputeOverflow]);

  const overflowKeys = useMemo(
    () => availableKeys.filter((key) => !visibleKeys.includes(key)),
    [availableKeys, visibleKeys]
  );

  const hasOverflow = overflowKeys.length > 0;
  const showOverflowMenu = isToolbarReady && (hasOverflow || isCompactMenuOpen);

  const handleOverflowMenuVisibleChange = useCallback(
    (open) => {
      menuOpenRef.current = open;
      setIsCompactMenuOpen(open);
      if (!open && !isDatePickerOpen) {
        scheduleRecomputeOverflow();
      }
    },
    [isDatePickerOpen, scheduleRecomputeOverflow]
  );

  const overflowMenuContent = useMemo(
    () => (
      <Menu
        theme={dark_Theme ? "dark" : "light"}
        selectable={false}
        mode="vertical"
      >
        {MENU_GROUPS.map((group) => {
          const groupKeys = group.keys.filter((key) =>
            overflowKeys.includes(key)
          );
          if (groupKeys.length === 0) return null;
          return (
            <Menu.ItemGroup
              key={group.title}
              title={
                <span className="pricer-filter-compact-menu__group-title">
                  {group.title}
                </span>
              }
            >
              {groupKeys.map((key) => menuRenderers[key]())}
            </Menu.ItemGroup>
          );
        })}
      </Menu>
    ),
    // menuRenderers close over handlers; overflowKeys drives menu contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overflowKeys, dark_Theme, productsData, buttonSelected, dateSelected]
  );

  const containerClassName = classnames(
    "buttons-container",
    "filter-buttons_container",
    "pricer-toolbar",
    dark_Theme || false
      ? "pricer-filter-buttons__container_dark"
      : "pricer-filter-buttons__container"
  );

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={{ left: 15, right: reserveRight }}
    >
      {/* Visible (on-bar) items */}
      {availableKeys
        .filter((key) => visibleKeys.includes(key))
        .map((key) => (
          <React.Fragment key={key}>{inlineRenderers[key]()}</React.Fragment>
        ))}

      {/* Overflow menu — Dropdown avoids Ant horizontal Menu auto-collapsing to "⋯" */}
      {showOverflowMenu && (
        <Dropdown
          trigger={["click"]}
          visible={isCompactMenuOpen || isDatePickerOpen}
          onVisibleChange={(open) => {
            if (isDatePickerOpen && !open) return;
            handleOverflowMenuVisibleChange(open);
          }}
          getPopupContainer={() => document.body}
          destroyPopupOnHide={false}
          overlay={overflowMenuContent}
          overlayClassName={classnames("pricer-filter-compact-menu-overlay", {
            "pricer-filter-compact-menu-overlay--dark": dark_Theme,
          })}
        >
          <span className="pricer-tools-trigger-wrap">
            <button
              type="button"
              className="pricer-tools-trigger"
              aria-haspopup="menu"
              aria-expanded={isCompactMenuOpen}
            >
              <MenuOutlined aria-hidden />
              <span className="pricer-tools-trigger__label">Pricer Tools</span>
            </button>
          </span>
        </Dropdown>
      )}

      {/* Offscreen measurer: always renders every available item to size them */}
      <div className="pricer-toolbar__measurer" aria-hidden>
        {availableKeys.map((key) => (
          <div
            key={key}
            ref={(el) => {
              measureRefs.current[key] = el;
            }}
            className="pricer-toolbar__measure-item"
          >
            {key === ITEM_KEYS.DATE
              ? inlineRenderers[key](true)
              : inlineRenderers[key]()}
          </div>
        ))}
        <div
          ref={menuMeasureRef}
          className="pricer-toolbar__measure-item pricer-toolbar__measure-menu"
        >
          <MenuOutlined /> Pricer Tools
        </div>
      </div>

      <CSVImportModal
        isOpen={isCSVImportModalOpen}
        onClose={setIsCSVImportModalOpen}
        refetchOptionRow={refetchOptionRow}
      />

      <QuickAdd
        isOpen={isQuickAddOpen}
        onClose={setIsQuickAddOpen}
        refetchOptionRow={refetchOptionRow}
        focusKey={quickAddFocusKey}
      />

      <TradeCaptureModal
        open={isTradeCaptureOpen}
        onClose={() => setIsTradeCaptureOpen(false)}
        refetchOptionRow={refetchOptionRow}
      />

      <VolSurfaceComparison
        variant="none"
        dark_Theme={dark_Theme}
        open={isVolComparisonOpen}
        onClose={() => setIsVolComparisonOpen(false)}
      />

      {/*Show Add Option Modal*/}
      {isAddOptionModalVisible && (
        <AddOptionModal
          setIsAddOptionModalVisible={setIsAddOptionModalVisible}
          isAddOptionModalVisible={isAddOptionModalVisible}
        />
      )}
    </div>
  );
};

export default FilterButtons;
